import { breakpoints, palette, pxToRem, spacing, typography } from '@fluentsms/agentnet-web-components';
import { red } from '@material-ui/core/colors';
import { emphasize, fade } from '@material-ui/core/styles/colorManipulator';
import { Overrides } from '@material-ui/core/styles/overrides';
import transitions from '@material-ui/core/styles/transitions';

const overrides: Overrides = {
  // APPBAR
  MuiAppBar: {
    colorSecondary: {
      backgroundColor: '#0b3b4a',
    },
  },

  // CARDS
  MuiCard: {
    root: {
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: palette.divider,
    },
  },
  MuiCardHeader: {
    root: {
      minHeight: spacing(7),
      padding: spacing(0.5, 3),
    },
    action: {
      marginTop: 0,
      marginRight: 0,
      alignSelf: 'center',
      alignItems: 'center',
      '& > [class*="MuiIconButton-root"]': {
        marginTop: -12,
        marginRight: -12,
        marginBottom: -12,
        '&:last-child': {
          marginRight: -16,
        },
      },
      '& [class*="MuiSwitch-root"]': {
        order: 2, // Reverse order of switch / label in card header actions
        marginRight: -27, // Align to right side of actions
      },
      '& [class*="MuiTextField-root"]': {
        marginTop: 0,
        paddingTop: spacing(0.375), // 3
      },
    },
    title: {
      textTransform: 'uppercase',
      minHeight: spacing(3),
      color: palette.text.secondary,
    },
  },
  MuiCardContent: {
    root: {
      paddingBottom: spacing(3),
      paddingLeft: spacing(3),
      paddingRight: spacing(3),
      paddingTop: spacing(3),
      '&:last-child': {
        paddingBottom: spacing(3),
      },
    },
  },
  MuiCardActions: {
    root: {
      padding: spacing(2, 3),
    },
  },

  // CHIPS

  MuiChip: {
    root: {
      backgroundColor: palette.divider,
      '& $avatarColorPrimary': {
        color: palette.primary.contrastText,
        backgroundColor: palette.primary.dark,
      },
      '& $avatarColorSecondary': {
        color: palette.primary.contrastText,
        backgroundColor: palette.primary.main,
      },
    },
    colorSecondary: {
      backgroundColor: palette.primary.light,
      color: palette.text.primary,
    },
    clickableColorSecondary: {
      '&:hover, &:focus': {
        backgroundColor: emphasize(palette.primary.light, 0.08),
      },
    },
    deleteIconColorSecondary: {
      color: fade(palette.primary.main, 0.7),
      '&:hover, &:active': {
        color: palette.primary.main,
      },
    },
    outlinedSecondary: {
      color: palette.text.primary,
      border: `1px solid ${palette.action.selected}`,
      '$clickable&:hover, $clickable&:focus, $deletable&:focus': {
        backgroundColor: palette.action.hover,
      },
    },
  },

  // CONTAINERS

  MuiContainer: {
    maxWidthLg: {
      // Custom content width utilized for majority of application.
      // Default container has maxWidthLg as well as contextual areas of L1 / L2 Nav.
      [breakpoints.up('lg')]: {
        maxWidth: '1440px',
      },
    },
    maxWidthXl: {
      // Custom content width utilized for special use within application.
      // Page content needing to utilize available width should be wrapped in a container with maxWidthXl.
      [breakpoints.only('xl')]: {
        maxWidth: 1920,
      },
    },

    root: {
      '&.fluid': {
        maxWidth: 'none',
        padding: 0,
      },
    },
  },

  // FORMS

  MuiInputLabel: {
    root: {
      ...typography.caption,
      fontSize: spacing(1.75),
      // These styles are targeting the input padding when a label is present
      '& ~ .MuiOutlinedInput-root .MuiOutlinedInput-input, & ~ .MuiFilledInput-root .MuiFilledInput-input': {
        height: '100%',
        paddingTop: spacing(1.25),
        paddingBottom: spacing(1),
      },
      '& ~ .MuiOutlinedInput-root .MuiOutlinedInput-inputMultiline, & ~ .MuiFilledInput-root .MuiFilledInput-inputMultiline': {
        paddingTop: spacing(1.25),
        paddingBottom: 0,
      },
      '&$marginDense ~ .MuiOutlinedInput-root .MuiOutlinedInput-input, &$marginDense ~ .MuiFilledInput-root .MuiFilledInput-input': {
        paddingTop: spacing(2.375),
        paddingBottom: spacing(0.75),
      },
      '& ~ .MuiOutlinedInput-root .MuiSelect-select, & ~ .MuiFilledInput-root .MuiSelect-select': {
        paddingBottom: spacing(0.625),
      },
      '&$error': {
        color: palette.error.dark,
      },
      '&$disabled': {
        background: 'linear-gradient(180deg, transparent 57%, #fafafa 43%)',
      },
    },
    formControl: {
      transform: 'translate(0, 1.69em) scale(1.34)',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      '&:not(.MuiInputLabel-shrink)': {
        maxWidth: 'calc(100% * .75)', // Invert transform value 1.34
      },
      '&$disabled': {
        color: palette.text.hint,
      },
    },
    shrink: {
      transform: 'translate(0, 1.5px) scale(1)',
      transformOrigin: 'top left',
      fontWeight: typography.fontWeightRegular,
      color: palette.text.primary,
      maxWidth: '100%',
      zIndex: 1,
    },
    outlined: {
      background: 'linear-gradient(180deg, transparent 57%, #fff 43%)',
      color: palette.text.secondary,
      paddingLeft: spacing(0.375),
      paddingRight: spacing(0.375),
      transform: 'translate(.85em, 1.0625em) scale(1)',
      '&$shrink': {
        transform: `translate(14px, -12px) scale(0.85)`,
      },
      '&$disabled': {
        color: palette.text.secondary,
      },
    },
    filled: {
      transform: 'translate(1.25em, 1em) scale(1.5)',
      '&$shrink': {
        transform: 'translate(1.33em, .5em) scale(1)',
      },
      '&$disabled': {
        color: palette.text.disabled,
      },
    },
    marginDense: {
      transform: 'translate(0, 1.3em) scale(1.34)',
      '&$shrink': {
        transform: 'translate(0, 1.5px) scale(1)',
      },
      '&$filled&$filled': {
        transform: 'translate(0.75em, 0.75em) scale(1.5)',
        '&$shrink': {
          transform: `translate(0.9375em, 0.3em) scale(1)`,
        },
      },
      '&$outlined&$outlined': {
        transform: 'translate(0.875em, 0.25em) scale(1)',
        '&$shrink': {
          transform: `translate(0.875em, 0.25em) scale(1)`,
        },
      },
    },
    animated: {
      transition: transitions.create(['color', 'transform', 'max-width'], {
        duration: transitions.duration.shorter,
        easing: transitions.easing.easeOut,
      }),
    },
  },
  MuiSelect: {
    select: {
      '&&:not($outlined)': {
        paddingTop: spacing(0.25), // 2,
        paddingBottom: spacing(0.375), // 3,
      },
      '&&[class*="MuiInput-inputMarginDense"]:not($outlined)': {
        paddingTop: 0,
        paddingBottom: spacing(0.25), // 2,
        lineHeight: 1.25,
      },
      '&:focus': {
        backgroundColor: palette.background.paper,
      },
      '&$disabled': {
        cursor: 'not-allowed',
      },
    },
    icon: {
      '&$disabled': {
        display: 'none',
      },
    },
    root: {
      '&.MuiFilledInput-input:focus': {
        backgroundColor: palette.primary.light,
      },
    },
  },
  MuiInput: {
    formControl: {
      /* Styles applied to the root element if the component is a descendant of `FormControl`. */
      'label + &': {
        marginTop: 19,
      },
    },
    underline: {
      '& .MuiSelect-icon': {
        top: 'calc(50% - 16px)',
      },
      '&:before': {
        borderBottomColor: palette.divider,
      },
      '& .MuiInputAdornment-root': {
        marginBottom: spacing(0.375),
        '& .MuiIconButton-root': {
          padding: 3,
          fontSize: spacing(2.25),
          marginBottom: spacing(0.5),
        },
      },
      '&:hover:not($disabled):before': {
        borderBottomColor: palette.secondary.light,
        '@media (hover: none)': {
          borderBottomColor: palette.divider,
        },
      },
      '& $disabled': {
        color: palette.text.hint,
        '-webkit-text-fill-color': palette.text.hint, // Fix Safari disabled input text inconsistancy
      },
    },
  },
  MuiInputBase: {
    input: {
      ...typography.body1,
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      color: palette.text.primary,
      padding: `${spacing(0.375)}px 0 ${spacing(0.875)}px`, // 3px 0 7px
      '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
        boxShadow: `0 0 0 20px ${palette.background.paper} inset`,
        '-webkit-text-fill-color': `${palette.text.secondary} !important`,
      },
      '&$disabled': {
        color: palette.text.disabled,
        cursor: 'not-allowed',
      },
    },
    inputMarginDense: {
      paddingTop: 0,
      paddingBottom: spacing(0.375), // 3,
    },
    inputMultiline: {
      whiteSpace: 'normal',
      textOverflow: 'initial',
      overflow: 'initial',
    },
    multiline: {
      padding: `${spacing(0)} !important`,
    },
  },
  MuiFilledInput: {
    root: {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: `${palette.primary.light}7F`,
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
      '&.Mui-focused': {
        backgroundColor: palette.primary.light,
      },
      '&.Mui-error': {
        backgroundColor: red[50],
      },
      '&.Mui-disabled': {
        backgroundColor: palette.brand.feather25,
        '& $input': {
          color: palette.text.hint,
        },
      },
    },
    adornedStart: {
      paddingLeft: 16,
    },
    adornedEnd: {
      paddingRight: 16,
    },
    input: {
      color: palette.text.primary,
      paddingTop: spacing(2),
      paddingRight: spacing(2),
      paddingBottom: spacing(2),
      paddingLeft: spacing(2),
    },
    inputMarginDense: {
      paddingTop: spacing(0.75),
      paddingRight: spacing(1.375),
      paddingBottom: spacing(0.75),
      paddingLeft: spacing(1.375),
    },
    underline: {
      '&:hover:before': {
        borderBottom: `1px solid ${palette.primary.light}`,
      },
      '&$error:after': {
        borderBottomColor: palette.primary.dark,
      },
      '&$disabled:before': {
        borderBottomColor: 'transparent',
      },
    },
  },
  MuiInputAdornment: {
    marginDense: {
      '&&$positionStart': {
        marginBottom: spacing(0.25), // 2,
        marginRight: 0,
      },
    },
  },
  MuiFormControl: {
    root: {
      marginBottom: spacing(0.25),
      marginTop: spacing(0.5),
    },
  },
  MuiOutlinedInput: {
    root: {
      backgroundColor: palette.background.paper,
      borderRadius: 4,
      '&$disabled': {
        backgroundColor: palette.brand?.feather25,
      },
      '&:hover $notchedOutline': {
        borderColor: `${palette.secondary.main}B2`,
      },
      '&$focused $notchedOutline': {
        borderColor: palette.primary.main,
        borderWidth: 2,
      },
      '&$error $notchedOutline': {
        borderColor: palette.error.dark,
      },
      '&$disabled $notchedOutline': {
        borderColor: `${palette.secondary.main}4C`,
      },
      '& .MuiOutlinedInput-inputAdornedEnd': {
        padding: spacing(1.15, 1),
      },
      '& > input[type="time"]::-webkit-calendar-picker-indicator': {
        filter: 'invert(34%) sepia(80%) saturate(1581%) hue-rotate(164deg) brightness(94%) contrast(101%)',
        left: '-3px',
        position: 'relative',
      },
    },
    input: {
      color: palette.text.primary,
      paddingTop: spacing(2),
      paddingRight: spacing(1.5),
      paddingBottom: spacing(2),
      paddingLeft: spacing(1.5),
    },
    notchedOutline: {
      borderColor: palette.secondary.light,
      '& > [class*="legendLabelled"] > span': {
        display: 'none',
      },
    },
    inputMarginDense: {
      paddingTop: spacing(0.875),
      paddingRight: spacing(1.375),
      paddingBottom: spacing(0.75),
      paddingLeft: spacing(1.375),
    },
    inputMultiline: {
      padding: `${spacing(1, 1.5, 0.75)} !important`,
    },
  },
  MuiAutocomplete: {
    root: {
      '@media (pointer: fine)': {
        '&:hover': {
          '& $input[value=""] + $endAdornment > $clearIndicatorDirty': {
            visibility: 'hidden',
          },
        },
        '&[class*="Mui-focused"]:hover $input[value=""] + $endAdornment > $clearIndicatorDirty': {
          visibility: 'visible',
        },
      },
    },
    hasClearIcon: {
      '& $inputRoot[class*="MuiInput-marginDense"]': {
        paddingRight: spacing(3.25), // 26,
        '& $endAdornment': {
          top: 'calc(50% - 15px)',
        },
      },
      '& [class*="MuiInputLabel-formControl"]:not(.MuiInputLabel-shrink)': {
        paddingRight: `calc(${spacing(3.5)}px * .75)`, // Invert transform value 1.34 -- Pad for right adornment
      },
      '&:not(:hover) [class*="MuiInputAdornment-positionEnd"]': {
        '& [class*="MuiIconButton"][class*="clearIndicator"]': {
          display: 'none',
        },
      },
      '&[class*="focused"]:not(:hover) [class*="MuiInputAdornment-positionEnd"]': {
        '& [class*="MuiIconButton"][class*="clearIndicator"]': {
          display: 'inherit',
        },
      },
    },
    inputRoot: {
      '$hasClearIcon:not($hasPopupIcon):not(:hover) &': {
        paddingRight: 0,
      },
      '$hasPopupIcon &, $hasClearIcon &, $hasPopupIcon$hasClearIcon &': {
        paddingRight: 26 + 4,
      },
      '$hasPopupIcon$hasClearIcon:hover &, $hasPopupIcon$hasClearIcon:focus-within &': {
        paddingRight: 52 + 4,
      },
      '&[class*="MuiInput-root"]': {
        paddingBottom: 1,
        '& $input': {
          padding: spacing(0.5), // 4,
        },
        '& $input:first-child': {
          padding: `${spacing(0.375)}px 0 ${spacing(0.75)}px`, // 3px 0 6px
        },
        '&[class*="MuiInputBase-multiline"] $input:first-child': {
          marginTop: -2,
          marginBottom: -2,
        },
        '&[class*="MuiInput-marginDense"] $input:first-child': {
          padding: `0 0 ${spacing(0.25)}px`, // 0 0 2px
        },
        '& $endAdornment': {
          top: 'calc(50% - 18px)',
        },
      },
      '&[class*="MuiOutlinedInput-root"]': {
        padding: spacing(0.8, 1, 1),
        '& $input': {
          padding: spacing(0.5, 0, 0, 1),
          '&:first-child': {
            paddingLeft: spacing(0.5),
          },
        },
      },
    },
  },
  MuiFormHelperText: {
    root: {
      color: palette.text.secondary,
      '&$disabled': {
        color: palette.text.disabled,
      },
      '&$error': {
        color: palette.error.dark,
      },
    },
    contained: {
      marginLeft: spacing(2),
      marginRight: spacing(2),
      '&$marginDense': {
        marginLeft: spacing(1.375),
        marginRight: spacing(1.375),
      },
    },
  },
  MuiSwitch: {
    switchBase: {
      '&:not($disabled):not($checked)': {
        '& [class*="MuiTouchRipple-root"],&:hover [class*="MuiTouchRipple-root"]': {
          color: palette.secondary.light,
        },
        '&$colorPrimary': {
          '& [class*="MuiTouchRipple-root"],&:hover [class*="MuiTouchRipple-root"]': {
            color: palette.primary.light,
          },
        },
        '&$colorSecondary': {
          '& [class*="MuiTouchRipple-root"],&:hover [class*="MuiTouchRipple-root"]': {
            color: palette.secondary.light,
          },
        },
      },
    },
  },

  // PICKERS

  MuiPickersCalendar: {
    // NOTE: Only way to style the calendar is through an override. "for-component" classes are not allowed.
  },
  // TABS

  MuiTab: {
    root: {
      ...typography.body2,
      padding: spacing(0, 1),
      textTransform: 'capitalize',
      '& [class*="MuiTypography-noWrap"]': {
        textTransform: 'none',
      },
      [breakpoints.up('sm')]: {
        minWidth: 140,
      },
    },
    textColorSecondary: {
      padding: spacing(0, 2),
      backgroundColor: `${palette.secondary.main}26`,
      transform: 'translateY(1px)',
      borderBottomWidth: 1,
      borderRightWidth: 1,
      borderColor: palette.divider,
      borderStyle: 'solid',
      color: palette.text.secondary,
      transition: transitions.create('border-color', {
        duration: transitions.duration.standard,
        easing: transitions.easing.easeInOut,
      }),
      '&$selected': {
        backgroundColor: palette.background.paper,
        color: palette.text.secondary,
      },
    },
    wrapper: {
      flexDirection: 'row',
      '& [class*="MuiIconButton-root"]': {
        marginLeft: spacing(1),
        marginRight: spacing(-1),
        '& *': {
          fontSize: pxToRem(20),
        },
      },
    },
    labelIcon: {
      minHeight: spacing(6),
      '& $wrapper > *:first-child': {
        marginBottom: 0,
        marginRight: spacing(1),
      },
    },
  },

  MuiTabs: {
    root: {
      '&:not([class*="MuiTabs-vertical"])': {
        position: 'relative',
        '& [class*="MuiTabs-flexContainer"]': {
          justifyContent: 'flex-start',
        },
        '& [class*="MuiTabs-scrollButtons"]': {
          position: 'absolute',
          zIndex: 100,
          top: 0,
          bottom: 0,
          backgroundColor: palette.background.default,
          transition: transitions.create('opacity', {
            duration: transitions.duration.shortest,
            easing: transitions.easing.easeInOut,
          }),
          '&:not([class*="Mui-disabled"])': {
            opacity: 1,
          },
          '&:first-child': {
            left: 0,
          },
          '&:last-child': {
            right: 0,
          },
        },
      },
    },
    flexContainer: {
      justifyContent: 'space-between',
    },
    scroller: {
      paddingBottom: 1,
    },
  },
};

export default overrides;
